import { getQueue } from './api'

// Coming back to a screen (Liam, 15 Oct 2026: "Work, the Diary and Messages pick up the other person's changes when you
// come back to them"). When the page comes to the front again, or the window gets focus back, the screen reads its data
// again, so a change Lewis made while Liam was elsewhere shows before Liam changes the same customer. Never while doing
// so would lose or hide something: a pop-up open, a box being typed in, the screen's own edit open (its `busy`), or one
// of this phone's own changes still on its way (a redraw then would show the row as it was before it landed). With no
// signal it waits for the next time. The screen's `read` is a quiet read: a read that fails leaves the screen as it is.

const MIN_GAP_MS = 2_000

export function ownChangeOnItsWay(): boolean {
  return getQueue().some((m) => m.status === 'sending' || m.status === 'queued')
}

// Something open on screen that a redraw would throw away.
export function somethingOpen(): boolean {
  if ([...document.querySelectorAll<HTMLDialogElement>('dialog')].some((d) => d.open)) return true
  const active = document.activeElement as HTMLElement | null
  if (!active || active === document.body) return false
  if (active.isContentEditable) return true
  const tag = active.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  // A search box is a filter, kept across the redraw; every other typing box is an edit.
  if (tag === 'INPUT') {
    const type = (active as HTMLInputElement).type || 'text'
    return type !== 'search' && !['button', 'submit', 'checkbox', 'radio', 'range'].includes(type)
  }
  return false
}

interface Watch {
  // The screen is the one on show.
  here: () => boolean
  // The screen's own edit is open (Work's cell, the Diary's editing, a Messages reply being written).
  busy: () => boolean
  read: () => void
}

const watches: Watch[] = []
let bound = false
let lastRead = 0

function comeBack(): void {
  if (document.visibilityState !== 'visible') return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  const now = Date.now()
  // The page coming to the front fires both events together: one read.
  if (now - lastRead < MIN_GAP_MS) return
  const watch = watches.find((w) => w.here())
  if (!watch) return
  if (somethingOpen() || watch.busy() || ownChangeOnItsWay()) return
  lastRead = now
  watch.read()
}

export function readAgainOnComeBack(watch: Watch): void {
  watches.push(watch)
  if (bound) return
  bound = true
  document.addEventListener('visibilitychange', comeBack)
  window.addEventListener('focus', comeBack)
}
